import {
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    useAnnounce,
} from '@fluentui/react-components';
import { Delete20Regular, Edit20Regular, MoreVertical20Filled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Report } from '../../../model';
import { cometDarkTheme, customPalette } from '../../../theme';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import { AlwaysDisplayClassName, DisplayOnActiveRowClassName } from '../StyledTable';
import NoCollateralWarningIcon from './NoCollateralWarningIcon';
import ReportLink from './ReportLink';

export interface IBrowseReportNameCellProps {
    item: Report;
    index: number | string | undefined;
    isCreateNewReturnEnabled: boolean | undefined;
    isEditCalculationEnhancementsEnabled: boolean | undefined;
    viewTaxReturnsEnabled: boolean | undefined;
    editReportsButtonEnabled: boolean | undefined;
    deleteReportsButtonEnabled: boolean | undefined;
    onEdit: (item: Report, index: number | string | undefined) => void;
    onDelete: (item: Report, index: number | string | undefined) => void;
}

const useStyles = makeStyles({
    cellContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ':focus-within [data-fui-focus-visible]': {
            textDecoration: 'underline',
            '& span': {
                color: customPalette.themePrimary,
            },
        },
    },
    nameContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        minWidth: 0,
        flex: '1 1 auto',
    },
    optionsButton: {
        border: 'none',
        display: 'none',
        '&[data-fui-focus-visible]': {
            border: `1px solid ${customPalette.themePrimary}`,
        },
        '& .ms-Button-flexContainer': {
            gap: '0px',
        },
    },
    reportNameText: {
        color: customPalette.blueDark,
        whiteSpace: 'nowrap',
        fontSize: '14px',
        lineHeight: '24px',
        fontWeight: 600,
    },
    disabledItem: {
        color: cometDarkTheme.colorNeutralStrokeDisabled,
    },
});

const BrowseReportNameCell = ({
    item,
    index,
    isCreateNewReturnEnabled,
    isEditCalculationEnhancementsEnabled,
    viewTaxReturnsEnabled,
    editReportsButtonEnabled,
    deleteReportsButtonEnabled,
    onEdit,
    onDelete,
}: IBrowseReportNameCellProps) => {
    const { t } = useTranslation();
    const { announce } = useAnnounce();
    const styles = useStyles();

    const onEditClick = () => onEdit(item, index);
    const onDeleteClick = () => onDelete(item, index);

    const makeMenuIconAlwaysVisible = (menuIconElement: HTMLElement) => {
        menuIconElement.classList.remove(DisplayOnActiveRowClassName);
        menuIconElement.classList.add(AlwaysDisplayClassName);
    };

    const makeMenuIconVisibleForActiveRows = (menuIconElement: HTMLElement) => {
        menuIconElement.classList.remove(AlwaysDisplayClassName);
        menuIconElement.classList.add(DisplayOnActiveRowClassName);
    };

    const announceNoFunctionalAccess = () => {
        announce(t('noFunctionalAccess').toString(), { batchId: `reports-no-functional-access-${item.id}` });
    };

    if (!viewTaxReturnsEnabled) {
        return (
            <DefaultTooltipHostV9
                content={t('noFunctionalAccess').toString()}
                relationship='label'
                alwaysDisplay
                containerClassName={styles.disabledItem}
            >
                <div className={styles.cellContainer}>
                    <label
                        tabIndex={0}
                        className={styles.disabledItem}
                        aria-disabled
                        onPointerEnter={announceNoFunctionalAccess}
                        onFocus={announceNoFunctionalAccess}
                    >
                        {item.name}
                    </label>
                    {isCreateNewReturnEnabled && (
                        <MoreVertical20Filled aria-label={t('noFunctionalAccess').toString()} className={styles.disabledItem} />
                    )}
                </div>
            </DefaultTooltipHostV9>
        );
    }

    return (
        <div className={styles.cellContainer}>
            <div className={styles.nameContainer}>
                {!item.canExecute && <NoCollateralWarningIcon item={item} />}
                <DefaultTooltipHostV9 content={item.name ?? ''} relationship='label'>
                    {!item.canExecute ? (
                        <label
                            aria-disabled
                            className={styles.reportNameText}
                            onPointerEnter={() => {
                                announce(t('unavailable').toString(), { batchId: `reports-unavailable-${item.id}` });
                            }}
                        >
                            {item.name}
                        </label>
                    ) : (
                        <ReportLink report={item} tabIndex={-1} />
                    )}
                </DefaultTooltipHostV9>
            </div>
            {isCreateNewReturnEnabled && (
                <Menu
                    onOpenChange={(event, data) => {
                        const target =
                            event.target instanceof HTMLElement
                                ? event.target.closest<HTMLElement>(`[data-testid="return-options-${index}"]`)
                                : null;
                        if (!target) {
                            return;
                        }
                        if (data.open) {
                            makeMenuIconAlwaysVisible(target);
                        } else {
                            makeMenuIconVisibleForActiveRows(target);
                        }
                    }}
                >
                    <MenuTrigger disableButtonEnhancement>
                        <MenuButton
                            appearance='subtle'
                            data-testid={`return-options-${index}`}
                            className={mergeClasses(styles.optionsButton, DisplayOnActiveRowClassName)}
                            menuIcon={<MoreVertical20Filled />}
                            aria-label={t('moreActionsForRow', { calcFileName: item.name }).toString()}
                        />
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            <MenuItem icon={<Edit20Regular />} disabled={!editReportsButtonEnabled} onClick={onEditClick}>
                                {t('editDetails').toString()}
                            </MenuItem>
                            {isEditCalculationEnhancementsEnabled && (
                                <MenuItem icon={<Delete20Regular />} disabled={!deleteReportsButtonEnabled} onClick={onDeleteClick}>
                                    {t('deleteCalculation').toString()}
                                </MenuItem>
                            )}
                        </MenuList>
                    </MenuPopover>
                </Menu>
            )}
        </div>
    );
};

export default BrowseReportNameCell;
